import { useCallback, useEffect, useRef, useState } from "react";
import { dailyNotesApi } from "../../services/dailyNotesApi";
import "./dailyNote.css";

// Ngừng gõ bao lâu thì lưu. 1s: đủ để gộp một chuỗi phím thành 1 request,
// vẫn kịp lưu trước khi admin kịp chuyển sang việc khác.
const SAVE_DELAY = 1000;

function pad2(n) {
  return String(n).padStart(2, "0");
}
function clockNow() {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function clockOf(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function formatDateStr(str) {
  const [y, m, d] = String(str).split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Ghi chú trong ngày ở trang Sổ thuê acc.
 * Lưu trên server (1 bản ghi/ngày) nên mở máy nào cũng thấy cùng nội dung.
 * Tự lưu khi ngừng gõ; rời ô hoặc đóng/ẩn tab cũng lưu nốt phần chưa kịp lưu.
 *
 * @param {string} date - ngày của ghi chú, dạng YYYY-MM-DD (giờ VN)
 */
export default function DailyNote({ date }) {
  const [content, setContent] = useState("");
  // loading | loadError | idle | saving | saved | saveError
  const [status, setStatus] = useState("loading");
  const [savedAt, setSavedAt] = useState("");
  const [confirming, setConfirming] = useState(false);

  const contentRef = useRef("");  // nội dung mới nhất trong ô
  const savedRef = useRef("");    // nội dung đang nằm trên server
  const timerRef = useRef(null);
  const dateRef = useRef(date);   // ngày hiện hành, dùng cho lần lưu chạy nền
  const loadedRef = useRef(false); // đã tải xong chưa — chưa xong thì cấm ghi đè

  // ── Tải ghi chú của ngày ────────────────────────────────────────
  const load = useCallback((d) => {
    setStatus("loading");
    loadedRef.current = false;
    dailyNotesApi
      .get(d)
      .then((data) => {
        if (dateRef.current !== d) return; // đã đổi ngày trong lúc chờ
        const text = data?.content || "";
        contentRef.current = text;
        savedRef.current = text;
        loadedRef.current = true;
        setContent(text);
        setSavedAt(clockOf(data?.updatedAt));
        setStatus("idle");
      })
      .catch(() => {
        if (dateRef.current === d) setStatus("loadError");
      });
  }, []);

  // ── Ghi lên server ──────────────────────────────────────────────
  const persist = useCallback((d, text) => {
    setStatus("saving");
    return dailyNotesApi
      .save(d, text)
      .then(() => {
        savedRef.current = text;
        // Trong lúc chờ mà admin gõ tiếp thì vẫn còn hẹn lưu đang chạy
        // → giữ nguyên "đang lưu", đừng báo đã lưu sớm.
        if (contentRef.current === text) {
          setSavedAt(clockNow());
          setStatus("saved");
        }
      })
      .catch(() => setStatus("saveError"));
  }, []);

  const flushNow = useCallback(() => {
    if (!loadedRef.current) return;
    if (contentRef.current === savedRef.current) return;
    clearTimeout(timerRef.current);
    persist(dateRef.current, contentRef.current);
  }, [persist]);

  const handleChange = (e) => {
    const value = e.target.value;
    contentRef.current = value;
    setContent(value);
    clearTimeout(timerRef.current);
    if (value === savedRef.current) {
      setStatus(savedAt ? "saved" : "idle");
      return;
    }
    setStatus("saving");
    timerRef.current = setTimeout(() => persist(dateRef.current, value), SAVE_DELAY);
  };

  const handleClear = () => {
    setConfirming(false);
    contentRef.current = "";
    setContent("");
    clearTimeout(timerRef.current);
    persist(dateRef.current, "");
  };

  // Đổi ngày (qua 0h tab vẫn mở) → lưu nốt ngày cũ rồi tải ghi chú ngày mới
  useEffect(() => {
    const prev = dateRef.current;
    if (prev !== date && loadedRef.current && contentRef.current !== savedRef.current) {
      clearTimeout(timerRef.current);
      dailyNotesApi.save(prev, contentRef.current).catch(() => {});
    }
    dateRef.current = date;
    setConfirming(false);
    load(date);
    return () => clearTimeout(timerRef.current);
  }, [date, load]);

  // Đóng tab / chuyển tab / rời trang: sendBeacon đi kèm vòng đời trang nên
  // không bị hủy giữa chừng như request thường.
  useEffect(() => {
    const flushBeacon = () => {
      if (!loadedRef.current) return;
      if (contentRef.current === savedRef.current) return;
      if (dailyNotesApi.saveBeacon(dateRef.current, contentRef.current)) {
        savedRef.current = contentRef.current;
      }
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flushBeacon();
    };
    window.addEventListener("pagehide", flushBeacon);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", flushBeacon);
      document.removeEventListener("visibilitychange", onHide);
      flushBeacon(); // rời khỏi trang Sổ thuê acc
    };
  }, []);

  const busy = status === "loading" || status === "loadError";
  const statusText =
    status === "loading" ? "Đang tải…"
      : status === "loadError" ? "Không tải được"
        : status === "saving" ? "Đang lưu…"
          : status === "saveError" ? "Chưa lưu được"
            : savedAt ? `Đã lưu ${savedAt}`
              : "Chưa có ghi chú";
  const statusTone =
    status === "saving" ? "saving"
      : status === "saveError" || status === "loadError" ? "error"
        : status === "saved" ? "saved"
          : "idle";

  return (
    <div className={`daily-note ${confirming ? "confirming" : ""}`}>
      <div className="daily-note-top">
        <span className="daily-note-title">
          <i className="far fa-sticky-note"></i> Ghi chú hôm nay
        </span>
        <span className="daily-note-date">{formatDateStr(date)}</span>
        <span className="daily-note-status" data-tone={statusTone}>
          <span className="daily-note-pip"></span>
          {statusText}
        </span>
        {status === "loadError" && (
          <button className="daily-note-retry" type="button" onClick={() => load(date)}>
            Tải lại
          </button>
        )}
        {status === "saveError" && (
          <button className="daily-note-retry" type="button" onClick={flushNow}>
            Lưu lại
          </button>
        )}
        <button
          className="daily-note-clear"
          type="button"
          title="Xóa ghi chú hôm nay"
          disabled={busy || !content.trim()}
          onClick={() => setConfirming(true)}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>

      <textarea
        className="daily-note-input"
        value={content}
        disabled={busy}
        placeholder="Ghi chú nhanh trong ngày… (vd: 5245 hẹn gia hạn 14h, 5212 khách chuyển khoản còn thiếu 25k)"
        onChange={handleChange}
        onBlur={flushNow}
      />

      {confirming && (
        <div className="daily-note-confirm">
          <span>Xóa ghi chú hôm nay?</span>
          <button className="daily-note-btn danger" type="button" onClick={handleClear}>Xóa</button>
          <button className="daily-note-btn" type="button" onClick={() => setConfirming(false)}>Hủy</button>
        </div>
      )}
    </div>
  );
}
